import { Spin } from "antd";
import { Clock, Plus, Search, User, X } from "lucide-react";
import { FC, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebounceCallback } from "~/hooks/use-debounce-callback";
import { TStaff } from "~/types";
import { StaffAvailabilitySchedule } from "~/types/staff-availability-schedule";
import { StaffAvailability } from "~/utils/constants";
import { formatStaffName } from "~/utils/format-staff-name";

interface StaffListProps {
  staffs: TStaff[];
  isLoading: boolean;
  onAddStaff: (staffId: string) => void;
  onRemoveStaff: (staffId: string) => void;
  isStaffAssigned: (staffId: string) => boolean;
  hasServiceSelected: boolean;
  loadMore: () => void;
  hasMore: boolean;
  onSearch: (term: string) => void;
  staffAvailabilityMap?: Map<string, StaffAvailabilitySchedule>;
  serviceDate?: string | null;
  hidePending?: boolean;
  hideUnavailable?: boolean;
  onToggleHidePending?: (value: boolean) => void;
  onToggleHideUnavailable?: (value: boolean) => void;
}

export const StaffList: FC<StaffListProps> = ({
  staffs,
  isLoading,
  onAddStaff,
  onRemoveStaff,
  isStaffAssigned,
  hasServiceSelected,
  loadMore,
  hasMore,
  onSearch,
  staffAvailabilityMap,
  serviceDate,
  hidePending,
  hideUnavailable,
  onToggleHidePending,
  onToggleHideUnavailable,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedCallback = useDebounceCallback(onSearch, 500);

  // Push the search term to parent when it changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedCallback(value);
  };

  const getAvailabilityBadge = (staffId: string, isAssigned: boolean) => {
    const availability = staffAvailabilityMap?.get(staffId);
    const bookedHours = availability?.bookedHours || 0;
    const availableHours = availability?.availableHours || 8;

    if (isAssigned) {
      // Show assigned badge with booked hours info
      if (serviceDate && availability && bookedHours > 0) {
        return (
          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 flex items-center gap-1">
            <Clock size={12} />
            Assigned ({bookedHours}h / {availableHours + bookedHours}h)
          </span>
        );
      }
      // Assigned but no availability record (pending)
      if (serviceDate && !availability) {
        return (
          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
            Assigned (pending)
          </span>
        );
      }
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
          Assigned
        </span>
      );
    }

    // No service selected - show generic available
    if (!serviceDate) {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
          Available
        </span>
      );
    }

    // Service selected but no availability record - pending
    if (!availability) {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
          Pending
        </span>
      );
    }

    // Staff marked as unavailable
    if (availability.availability === StaffAvailability.UNAVAILABLE) {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
          Unavailable
        </span>
      );
    }

    // Show availability status with hours
    const isPartTime = availability.availability === StaffAvailability.PART_TIME;

    if (bookedHours > 0) {
      // Color code based on how booked they are
      const isNearlyBooked = availableHours <= 2;
      const isPartiallyBooked = bookedHours >= 3;

      return (
        <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${
          isNearlyBooked ? "bg-red-100 text-red-800" :
          isPartiallyBooked ? "bg-amber-100 text-amber-800" :
          "bg-yellow-100 text-yellow-800"
        }`}>
          <Clock size={12} />
          {bookedHours}h booked / {availableHours}h free
        </span>
      );
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${
        isPartTime ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"
      }`}>
        {isPartTime ? "Part-time" : "Full-time"} ({availableHours}h)
      </span>
    );
  };

  return (
    <div className="p-4">
      <div className="mb-4 relative">
        <input
          type="text"
          className="w-full pl-10 pr-4 py-2 border rounded-lg"
          placeholder="Search staff..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
      </div>

      {/* Show placeholder when no service is selected */}
      {!hasServiceSelected ? (
        <div className="flex flex-col items-center justify-center h-96 text-gray-500">
          <User size={48} className="mb-4 text-gray-300" />
          <p className="text-lg font-medium">No Service Selected</p>
          <p className="text-sm text-center mt-2">
            Select a service from the left panel to view<br />available staff for that date
          </p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="mb-4 flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hidePending}
                onChange={(e) => onToggleHidePending?.(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-gray-600">Hide Pending</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hideUnavailable}
                onChange={(e) => onToggleHideUnavailable?.(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-gray-600">Hide Unavailable</span>
            </label>
          </div>

          <Spin spinning={isLoading && staffs.length === 0}>
        <InfiniteScroll
          dataLength={staffs.length}
          next={loadMore}
          hasMore={hasMore}
          loader={<Spin className="m-5" />}
          height={384}
          endMessage={
            <p className="text-center text-gray-500 mt-4">
              No more staff to load
            </p>
          }
        >
          {staffs.map((staff) => (
            <div
              key={staff.id}
              className="flex justify-between items-center p-3 border rounded-lg m-2"
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <User size={16} />
                </div>
                <div>
                  <div className="font-medium">
                    {formatStaffName(staff)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {staff?.staffRole?.name || "Staff"}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {getAvailabilityBadge(staff.id, isStaffAssigned(staff.id))}
                {hasServiceSelected && !isStaffAssigned(staff.id) && (
                  <button
                    className="p-1 text-blue-600 hover:text-blue-800"
                    onClick={() => onAddStaff(staff.id)}
                  >
                    <Plus size={16} />
                  </button>
                )}
                {hasServiceSelected && isStaffAssigned(staff.id) && (
                  <button
                    className="p-1 text-red-600 hover:text-red-800"
                    onClick={() => onRemoveStaff(staff.id)}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </InfiniteScroll>
      </Spin>
        </>
      )}

      <div className="mt-4 pt-4 border-t">
        <button className="w-full flex items-center justify-center space-x-2 p-3 border rounded-lg hover:bg-gray-50">
          <Plus size={16} />
          <span>Add New Staff Member</span>
        </button>
      </div>
    </div>
  );
};
